import React, { useState } from "react";
import { X, AlertTriangle, Tag, Clock } from "lucide-react";
import { useCreateBanRequest } from "../../hooks/useBanRequests";
import { Customer } from "../../types/customer";

interface BanRequestModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  banType: "under_monitoring" | "temporary_ban" | "permanently_banned";
}

export const BanRequestModal: React.FC<BanRequestModalProps> = ({
  customer,
  isOpen,
  onClose,
  banType,
}) => {
  const [remarks, setRemarks] = useState("");
  const [banEndDate, setBanEndDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createBanRequestMutation = useCreateBanRequest();

  if (!customer) return null;

  const getBanTypeInfo = () => {
    switch (banType) {
      case "under_monitoring":
        return {
          title: "Place Under Monitoring",
          icon: <Tag className="w-5 h-5 text-yellow-400" />,
          description: "This customer will be flagged for monitoring.",
          color: "yellow",
        };
      case "temporary_ban":
        return {
          title: "Temporary Ban Request",
          icon: <Clock className="w-5 h-5 text-orange-400" />,
          description: "Request a temporary ban for this customer.",
          color: "orange",
        };
      case "permanently_banned":
        return {
          title: "Permanent Ban Request",
          icon: <AlertTriangle className="w-5 h-5 text-red-400" />,
          description: "Request a permanent ban for this customer.",
          color: "red",
        };
    }
  };

  const typeInfo = getBanTypeInfo();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!remarks.trim()) return;

    setIsSubmitting(true);
    try {
      await createBanRequestMutation.mutateAsync({
        customerId: customer.id,
        banType,
        banEndDate: banType === "temporary_ban" ? banEndDate : undefined,
        remarks: remarks.trim(),
      });

      setRemarks("");
      setBanEndDate("");
      onClose();
    } catch (error) {
      console.error("Failed to create ban request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRemarks("");
    setBanEndDate("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              {typeInfo.icon}
              <h2 className="text-lg font-semibold text-white">
                {typeInfo.title}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6">
            {/* Customer Info */}
            <div className="mb-4 p-3 bg-slate-700 rounded-lg">
              <div className="text-sm text-slate-400">Customer</div>
              <div className="text-white font-medium">{customer.name}</div>
              <div className="text-sm text-slate-300">{customer.email}</div>
            </div>

            {/* Description */}
            <div className="mb-4">
              <p className="text-slate-300 text-sm">{typeInfo.description}</p>
            </div>

            {/* Ban End Date (only for temporary ban) */}
            {banType === "temporary_ban" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Ban End Date *
                </label>
                <input
                  type="datetime-local"
                  value={banEndDate}
                  onChange={(e) => setBanEndDate(e.target.value)}
                  required
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {/* Remarks */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Reason / Remarks *
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Please provide a detailed reason for this request..."
                required
                rows={4}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  isSubmitting ||
                  !remarks.trim() ||
                  (banType === "temporary_ban" && !banEndDate)
                }
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  typeInfo.color === "yellow"
                    ? "bg-yellow-600 hover:bg-yellow-700"
                    : typeInfo.color === "orange"
                      ? "bg-orange-600 hover:bg-orange-700"
                      : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isSubmitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
